import React, { useEffect } from "react";
import { EditorialPage, Polaroid } from "../components/editorial";
import StoryPageHero from "../components/story/StoryPageHero";
import MeshField from "../components/story/MeshField";
import Reveal from "../components/story/Reveal";
import Highlighter from "../components/story/Highlighter";
import Doodle from "../components/story/Doodle";
import FounderFilmPlayer from "../components/story/FounderFilmPlayer";
import Navbar from "../components/Navbar";
import DownloadAct from "../components/story/DownloadAct";

// The public company page: who we are, why this exists, what we did before we
// built it, and how it pays for itself.
//
// WHERE THE WORDS COME FROM. Every line of copy here is lifted or lightly
// tightened from the founders' own material in threads-letsloop, deliverables/
// LL-0219-funding-command-centre: 07-founder-bios.md (whose "Together" block is
// assigned to "Website about" by that file itself), 01-application-answer-bank.md
// (problem, solution, achieved-so-far, impact) and 03-founder-video-pack.md
// (the two-founder script), and Joshua's bio from his own founder story,
// threads-letsloop deliverables/LL-0226-josh-founder-story (dump.md is his
// verbatim words; narrative.md and the grant answers are the drafts he
// corrected on 2026-08-23), and Adrien's from his own vault/
// About_me_Adrien_Founder.md plus the founder's additions (data, proptech).
// Nothing on this page was invented for it. If a fact changes, change the
// source and this page together.
//
// AGE AND ETHNICITY ARE DELIBERATELY ABSENT. The source bios carry both (they
// are load-bearing for grant eligibility), and 07-founder-bios.md itself says
// public bios are a separate consent. Founder decision 2026-09-03: not on the
// public site. Do not re-add them from the source.
//
// NO ROLE SPLIT EITHER. The source bios divide the work ("built the product",
// "runs the community side"). Founder, 2026-09-03: "not strictly true ... we
// both contributed to all of it." The bios are personal motivation, not job
// descriptions. Do not reintroduce who-built-what.
//
// WHY IT IS BUILT LIKE THE HOME STORY. The first draft was two flat columns and
// a grey facts card, which is exactly what the site's design language exists to
// avoid: consecutive prose rows read as documentation, so the acts sit on
// alternating tinted grounds, Fraunces runs at display size, marks float, and a
// coral highlighter lands on the one phrase per act that matters. Same
// construction as HowItWorksPage and StoryHomePage.
//
// NO ATTENDANCE FIGURES. The answer bank carries them for grant forms; the
// founder's rule for the website (2026-09-03) is "don't over tell, no one goes
// into that much detail". "Five times" and "we spent nothing" are the whole
// story here. Do not re-add the counts from the source.
//
// LOAD-BEARING CLAIMS. Programme reviewers and partners read this page next to
// the Companies House record. The registration line is duplicated in
// components/story/DownloadAct.tsx (the site footer) and was verified against
// the register on 2026-08-11: LETSLOOP LTD, 17178512, incorporated 24 April
// 2026. Do not edit either copy without re-checking Companies House.

const COMPANY_NUMBER = "17178512";
const COMPANIES_HOUSE_URL = `https://find-and-update.company-information.service.gov.uk/company/${COMPANY_NUMBER}`;
const CONTACT_EMAIL = "hello@letsloop.app";

type Founder = {
  name: string;
  role: string;
  bio: string;
  /** Real, served image path, or null when no photo exists yet. */
  photo: string | null;
  rotate: string;
};

// Photos are opt-in per person BECAUSE A MISSING FILE FAILS SILENTLY.
// content/people.ts points founders at /people/<slug>.jpg, and a path with no
// file behind it falls through to the SPA and returns HTML with a 200, so the
// <img> renders broken rather than erroring loudly. Both founders now have a
// real file (joshua.jpg 2026-09-03, adrien.jpg 2026-09-04), which also fills
// in their /people cards. The monogram below is a designed state, not a
// placeholder to forget: to add a person, drop a square file in public/people/
// and set `photo` — leave it null until the file actually exists.
const FOUNDERS: Founder[] = [
  {
    name: "Joshua Oakley",
    role: "Co-founder",
    bio: "Grew up in London, left for university and came back to find how hard it is to make friends as an adult. He co-founded a social care business supporting adults with learning disabilities and autism, works in data, and has built and raised for other founders before. LetsLoop is the first time it all points at the thing he cares about most: the void after university, where friendship becomes work.",
    photo: "/people/joshua.jpg",
    rotate: "-rotate-2",
  },
  {
    name: "Adrien Francis",
    role: "Co-founder",
    bio: "Raised in London and trained as an architect at Plymouth, which is where the interest in how physical places bring people together started. Since then he has worked across design, data and proptech, and now builds agentic AI systems and products for businesses. He has lived and worked across the UK and Europe, and moved cities enough times to know exactly what the app is for.",
    photo: "/people/adrien.jpg",
    rotate: "rotate-2",
  },
];

// The three by-hand events we have pictures of. Captions describe the picture,
// not invented attendance or dates.
const EVENT_POLAROIDS = [
  { src: "/polaroid-hackney.webp", caption: "Hackney", rotate: "-rotate-3" },
  { src: "/polaroid-pub.webp", caption: "Quiz night", rotate: "rotate-2" },
  { src: "/polaroid-sunday.webp", caption: "A Sunday", rotate: "-rotate-1" },
];

// Activity types only, the same rule LoopMarquee follows: no invented attendee
// counts, times or venues, because inventing those is fabricating social proof.
const LOOP_CHIPS = [
  "A coffee",
  "A walk",
  "A 6:30 kickabout",
  "A pub quiz",
  "A park run",
  "Dinner with strangers",
  "Someone's gig",
  "A gallery wander",
];

function initials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");
}

const Kicker: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-salmon-dark">
    {children}
  </p>
);

const ActTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = "",
}) => (
  <h2
    className={`font-headline text-ink font-bold leading-[1.1] tracking-[-0.025em] text-[clamp(2rem,4.6vw,3.2rem)] text-balance ${className}`}
  >
    {children}
  </h2>
);

/** Both founders get the SAME frame whether or not a photo exists, so the pair
 *  reads as one designed object rather than a photo beside a gap. */
const FounderCard: React.FC<{ founder: Founder }> = ({ founder }) => (
  <div>
    <div
      className={`polaroid-frame inline-block p-3 pb-4 shadow-xl transform-gpu transition-transform duration-300 hover:rotate-0 ${founder.rotate}`}
    >
      {founder.photo ? (
        <img
          src={founder.photo}
          alt={founder.name}
          loading="lazy"
          className="block h-40 w-40 object-cover md:h-48 md:w-48"
        />
      ) : (
        <div
          aria-hidden="true"
          className="flex h-40 w-40 items-center justify-center bg-cream font-headline text-5xl font-bold text-salmon-dark md:h-48 md:w-48"
        >
          {initials(founder.name)}
        </div>
      )}
    </div>
    <h3 className="mt-8 font-headline text-3xl font-bold leading-tight text-ink">
      {founder.name}
    </h3>
    <p className="mt-2 text-xs font-bold uppercase tracking-[0.25em] text-salmon-dark">
      {founder.role}
    </p>
    <p className="mt-4 max-w-md text-lg leading-relaxed text-ink/75">
      {founder.bio}
    </p>
  </div>
);

export default function AboutPage() {
  useEffect(() => {
    document.title = "About LetsLoop | We are the users, not observers of them";
  }, []);

  return (
    <>
      <Navbar />
      <EditorialPage>
        {/* 1 · THE THESIS ----------------------------------------------- */}
        <StoryPageHero
          kicker="who we are"
          title={
            <>
              We are the users,{" "}
              <Highlighter tone="coral">not observers of them.</Highlighter>
            </>
          }
          lede={
            <>
              Moving countries, moving cities and graduating into a place where
              you know nobody are three of the biggest loneliness triggers there
              are. Between us we have lived all three.
            </>
          }
          marks={[
            {
              name: "burst",
              size: 42,
              className: "right-[9%] top-[20%] text-salmon/70 story-float",
            },
            {
              name: "underline",
              size: 96,
              rotate: -3,
              className: "left-[6%] bottom-[12%] text-teal/30",
            },
          ]}
        />

        {/* 2 · THE FILM --------------------------------------------------- */}
        <section className="relative w-full overflow-hidden bg-cream px-6 py-24 md:px-12 md:py-32">
          <MeshField variant="ambient" />
          <Doodle
            name="squiggle"
            size={64}
            rotate={-8}
            className="absolute left-[5%] top-[12%] hidden text-teal/30 story-float md:block"
          />
          <div className="relative mx-auto grid max-w-6xl items-center gap-12 md:grid-cols-2 md:gap-16">
            <Reveal y={26}>
              <Kicker>The people behind it</Kicker>
              <ActTitle>
                Two friends who found value in{" "}
                <Highlighter tone="coral">the small things.</Highlighter>
              </ActTitle>
              <p className="mt-6 max-w-md text-lg leading-relaxed text-ink/75">
                A coffee after work, a Sunday pub quiz, a walk that turns into
                dinner. We built LetsLoop so those are easier for everyone else
                to find.
              </p>
              <p className="mt-4 text-sm text-ink/70">
                Fifty seconds, filmed around London. It plays when you reach it,
                and the sound stays off unless you ask for it.
              </p>
            </Reveal>
            <Reveal y={30} delay={120} className="relative">
              <FounderFilmPlayer className="max-w-[340px] md:max-w-[420px]" />
            </Reveal>
          </div>
        </section>

        {/* 3 · THE PROBLEM, IN OUR WORDS ---------------------------------- */}
        <section className="ground-butter relative overflow-hidden px-6 py-20 md:px-12 md:py-28">
          <Doodle
            name="loop"
            size={70}
            rotate={12}
            className="absolute right-[6%] bottom-[10%] hidden text-ink/15 md:block"
          />
          <div className="mx-auto max-w-6xl">
            <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-12 md:gap-16">
              <Reveal y={26} className="md:col-span-7">
                <Kicker>The problem</Kicker>
                <blockquote className="border-l-4 border-salmon pl-6 md:pl-8">
                  <p className="font-headline text-[clamp(1.5rem,3.2vw,2.4rem)] font-semibold leading-[1.25] tracking-[-0.015em] text-ink text-balance">
                    It does not feel like loneliness. You are surrounded by
                    people. You have a full phone. You just cannot get anyone
                    into the same room on a Tuesday.
                  </p>
                </blockquote>
                <p className="mt-6 max-w-lg text-lg leading-relaxed text-ink/75">
                  Making a plan now takes a group chat, a shared calendar and
                  about four days. Most small plans die before they happen. Not
                  because nobody wants to come. Because asking is too much
                  effort.
                </p>
              </Reveal>
              <Reveal y={26} delay={120} className="md:col-span-5">
                <div className="md:pl-6">
                  <p
                    className="font-headline text-[clamp(4.5rem,11vw,7.5rem)] font-bold leading-none tracking-[-0.04em] text-ink"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    31%
                  </p>
                  <p className="mt-3 max-w-xs text-base leading-relaxed text-ink/75">
                    of 16 to 29 year olds are lonely often or always. The
                    highest of any age group, and almost all funded help points
                    at people forty years older.
                  </p>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">
                    ONS, Community Life / loneliness data, 2025
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* 4 · WE RAN IT BY HAND ------------------------------------------ */}
        <section className="ground-mint relative overflow-hidden px-6 py-20 md:px-12 md:py-28">
          <Doodle
            name="arrow"
            size={64}
            rotate={18}
            className="absolute left-[5%] top-[14%] hidden text-salmon/60 story-float md:block"
          />
          <div className="mx-auto max-w-6xl">
            <Reveal y={26}>
              <Kicker>Before the app</Kicker>
              <ActTitle className="max-w-3xl">
                Five times,{" "}
                <Highlighter tone="coral">before we let ourselves</Highlighter>{" "}
                build it.
              </ActTitle>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 items-center gap-12 md:grid-cols-12 md:gap-16">
              <Reveal y={26} delay={80} className="md:col-span-5">
                <p className="text-lg leading-relaxed text-ink/75">
                  People turned up every time, and we spent nothing getting them
                  there.
                </p>
                <p className="mt-5 text-lg leading-relaxed text-ink/75">
                  That was the bit we needed to know. Not whether people would
                  download something. Whether they would actually leave the
                  house.
                </p>
              </Reveal>
              <div className="md:col-span-7">
                {/* Real photos from the by-hand events, scattered the way the
                    Events page scatters them: each straightens on hover. */}
                <div className="grid grid-cols-3 items-end gap-3 md:gap-5">
                  {EVENT_POLAROIDS.map((p, i) => (
                    <Reveal
                      key={p.src}
                      y={30}
                      delay={140 + i * 90}
                      className={`transform-gpu transition-transform duration-300 hover:rotate-0 ${p.rotate} ${
                        i === 1 ? "md:-translate-y-6" : ""
                      }`}
                    >
                      <Polaroid src={p.src} caption={p.caption} />
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 5 · THE TWO OF US ---------------------------------------------- */}
        <section className="ground-peach relative overflow-hidden px-6 py-20 md:px-12 md:py-28">
          <Doodle
            name="star"
            size={28}
            className="absolute right-[8%] top-[16%] hidden text-salmon story-float md:block"
          />
          <div className="mx-auto max-w-6xl">
            <Reveal y={26}>
              <Kicker>The two of us</Kicker>
              <ActTitle className="max-w-2xl">
                Building it <Highlighter tone="coral">in London.</Highlighter>
              </ActTitle>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 gap-14 md:grid-cols-2 md:gap-16">
              {FOUNDERS.map((founder, i) => (
                <Reveal key={founder.name} y={26} delay={90 * (i + 1)}>
                  <FounderCard founder={founder} />
                </Reveal>
              ))}
            </div>
            <Reveal y={22} delay={260}>
              <p className="mt-16 max-w-4xl font-headline text-[clamp(1.35rem,2.6vw,1.9rem)] font-semibold leading-[1.3] tracking-[-0.01em] text-ink text-balance">
                We ran LetsLoop by hand five times before we let ourselves build
                it, because the hard part of this is not getting downloads. It
                is getting people out of the house.
              </p>
            </Reveal>
          </div>
        </section>

        {/* 6 · FREE WHERE IT COUNTS --------------------------------------- */}
        <section className="relative overflow-hidden bg-cream px-6 py-20 md:px-12 md:py-28">
          <Doodle
            name="heart"
            size={30}
            className="absolute left-[7%] top-[18%] hidden text-salmon/60 md:block"
          />
          <div className="mx-auto max-w-6xl">
            <Reveal y={26}>
              <Kicker>How it works as a business</Kicker>
              <ActTitle className="max-w-3xl">
                Free where it counts,{" "}
                <Highlighter tone="coral">
                  paid where it makes sense.
                </Highlighter>
              </ActTitle>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-16">
              <Reveal y={26} delay={80} className="md:col-span-6">
                <p className="text-lg leading-relaxed text-ink/75">
                  The core is free and stays free. We monetise the venues who
                  gain footfall, not the people who show up: optional premium
                  features for power users, and community-aligned venue and
                  event partnerships.
                </p>
              </Reveal>
              <Reveal y={26} delay={160} className="md:col-span-6">
                {/* The funding position is the line a reviewer or a partner
                    actually looks for, so it is set as a statement rather than
                    buried in a run of body copy. */}
                <p className="border-l-4 border-salmon pl-6 font-headline text-[clamp(1.2rem,2.2vw,1.55rem)] font-semibold leading-[1.35] text-ink">
                  LetsLoop is currently pre-funded, and intends to pursue
                  venture funding when adoption and retention support a credible
                  raise.
                </p>
              </Reveal>
            </div>
          </div>

          {/* What a loop is, as a drifting band: the same device the home page
              uses (LoopMarquee), same class names, same CSS. */}
          <div
            className="ll-marquee mt-16"
            aria-label="Things people loop in to"
          >
            <div className="ll-track">
              {[...LOOP_CHIPS, ...LOOP_CHIPS, ...LOOP_CHIPS].map((label, i) => (
                <span
                  key={`${label}-${i}`}
                  className="mr-4 inline-flex items-center rounded-full border border-hairline/50 bg-white/60 px-6 py-3 font-headline text-lg font-semibold text-ink whitespace-nowrap"
                >
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* 7 · THE COMPANY: one quiet line. The statutory disclosure lives in
              the footer on every page now, so this only owes a reviewer the
              verifiable pointer. */}
          <Reveal y={16} delay={200}>
            <p className="mx-auto mt-16 max-w-6xl text-sm leading-relaxed text-ink/60">
              LetsLoop is a trading name of LETSLOOP LTD, registered in England
              and Wales, company number {COMPANY_NUMBER}.{" "}
              <a
                href={COMPANIES_HOUSE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-ink underline decoration-ink/30 underline-offset-4 transition-colors hover:text-salmon-dark"
              >
                See the Companies House record
              </a>
              , or write to{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-semibold text-ink underline decoration-ink/30 underline-offset-4 transition-colors hover:text-salmon-dark"
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Reveal>
        </section>
      </EditorialPage>

      {/* The teal act is the site footer: store CTAs, wordmark, legal strip. */}
      <DownloadAct />
    </>
  );
}
